import {sendTrackingMessage} from '../TrackingMessage';
import * as Sentry from '@sentry/browser';
import {TRACKING_EVENT_TYPE, type TrackingEventType} from './Constants';
import {trackEvent} from '../amplitude/TrackingV2';
import {TRACKING_OBJECTS} from '../amplitude/constants/TrackingObjects';
import {SEARCH_RESULT_VARIANT} from '../../forecast-app/navigation/header/global-search/SearchResultRow';
import {
	type SearchQueryResult,
	type AccessibleProjects,
} from '@/forecast-app/navigation/header/global-search/types/SearchResult';

const getTrackingEventTypeFromTrackingObject = (trackingObject: string): TrackingEventType => {
	switch (trackingObject) {
		case TRACKING_OBJECTS.TIME_REGISTRATION_MODAL_SEARCH:
			return TRACKING_EVENT_TYPE.TIME_REGISTRATION_MODAL_SEARCH_RESULT_SELECTED;
		default:
			return TRACKING_EVENT_TYPE.GLOBAL_SEARCH_RESULT_SELECTED;
	}
};

interface SelectedItem {
	id: string;
	variant: string;
	isSuggestion: boolean;
}

interface TrackGlobalSearchArgs {
	trackingObject: string;
	companyId: string | null;
	personId: number | null;
	searchString: string;
	searchStringHistory: string[];
	selectedItem: SelectedItem;
	searchResults: SearchQueryResult[];
	accessibleProjectIds: AccessibleProjects;
	firstInSession: boolean;
}

const constructGlobalSearchSessionEndedBody = ({
	trackingObject,
	companyId,
	personId,
	searchString,
	searchStringHistory,
	selectedItem,
	searchResults,
	accessibleProjectIds,
	firstInSession,
}: TrackGlobalSearchArgs) => {
	return {
		eventType: getTrackingEventTypeFromTrackingObject(trackingObject),
		companyId: companyId,
		searchPersonId: personId,
		searchString,
		searchStringHistory,
		selectedItem,
		searchResults,
		accessibleProjectIds,
		firstInSession,
	};
};

const getSelectionVariantString = (selectedItem: SelectedItem): string => {
	if (selectedItem.isSuggestion) return 'Suggestion';
	switch (selectedItem.variant) {
		case SEARCH_RESULT_VARIANT.PROJECT:
		case SEARCH_RESULT_VARIANT.PROJECT_GROUP:
			return 'Project';
		case SEARCH_RESULT_VARIANT.TASK:
			return 'Task';
		default:
			return 'Result';
	}
};

export const trackGlobalSearch = (args: TrackGlobalSearchArgs) => {
	try {
		const trackingAction = args.selectedItem
			? `${getSelectionVariantString(args.selectedItem)} Selected`
			: 'Closed Without Selection';
		trackEvent(args.trackingObject, trackingAction, {
			searchString: args.searchString,
			selectedItem: args.selectedItem,
			firstInSession: args.firstInSession,
		});

		if (args.searchString || args.selectedItem) {
			const s3Body = constructGlobalSearchSessionEndedBody(args);
			sendTrackingMessage(s3Body);
		}
	} catch (err) {
		Sentry.captureException(err);
		console.log('Tracking failed, reported to Sentry', err);
	}
};
